import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { IWorkshopType } from '../interface/Workshop';

export const fetchWorkshopTypes = async (
    searchQuery: string,
    currentPage: number,
    itemsPerPage: number
): Promise<{ data: IWorkshopType[]; totalItems: number }> => {
    try {
        const response = await axiosInstance.get('/WorkshopType', {
            params: {
                search: searchQuery,
                page: currentPage,
                pageSize: itemsPerPage,
            },
        });

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to fetch workshop types');
        }

        return {
            data: response.data.data,
            totalItems: response.data.totalItems,
        };
    } catch (error) {
        let errorMessage = 'An unexpected error occurred while fetching workshop types.';
        if (error instanceof AxiosError) {
            errorMessage = error.response?.data?.message || errorMessage;
        } else if (error instanceof Error) {
            errorMessage = error.message;
        }
        toast.error(errorMessage);
        throw new Error(errorMessage);
    }
};

export const getWorkshopTypeById = async (id: number): Promise<IWorkshopType> => {
    try {
        const response = await axiosInstance.get(`/WorkshopType/${id}`);

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to retrieve workshop type');
        }

        return response.data.data;
    } catch (error: unknown) {
        let errorMessage = 'An unexpected error occurred while retrieving the workshop type.';
        if (error instanceof AxiosError) {
            errorMessage = error.response?.data?.message || errorMessage;
        } else if (error instanceof Error) {
            errorMessage = error.message || errorMessage;
        }
        toast.error(errorMessage);
        throw new Error(errorMessage);
    }
};

export const createWorkshopType = async (data: Omit<IWorkshopType, 'workshopTypeID'>): Promise<IWorkshopType> => {
    try {
        const response = await axiosInstance.post('/WorkshopType', data);
        if (response.data.success) {
            toast.success(response.data.message || 'Workshop type created successfully');
            return response.data.data;
        } else {
            throw new Error(response.data.message);
        }
    } catch (error) {
        let errorMessage = 'An unexpected error occurred while creating workshop type.';
        if (error instanceof AxiosError) {
            errorMessage = error.response?.data?.message || errorMessage;
        } else if (error instanceof Error) {
            errorMessage = error.message || errorMessage;
        }
        toast.error(errorMessage);
        throw error;
    }
};

export const updateWorkshopType = async (id: number, data: IWorkshopType): Promise<IWorkshopType> => {
    try {
        const response = await axiosInstance.put(`/WorkshopType/${id}`, data);

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to update workshop type');
        }

        toast.success(response.data.message || 'Workshop type updated successfully');
        return response.data.data;
    } catch (error: unknown) {
        let errorMessage = 'An unexpected error occurred while updating the workshop type.';
        if (error instanceof AxiosError) {
            errorMessage = error.response?.data?.message || errorMessage;
        } else if (error instanceof Error) {
            errorMessage = error.message || errorMessage;
        }
        toast.error(errorMessage);
        throw new Error(errorMessage);
    }
};

export const deleteWorkshopType = async (id: number): Promise<void> => {
    try {
        const response = await axiosInstance.delete(`/WorkshopType/${id}`);

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to delete workshop type');
        }

        toast.success(response.data.message || 'Workshop type deleted successfully');
    } catch (error: unknown) {
        let errorMessage = 'An unexpected error occurred while deleting the workshop type.';
        if (error instanceof AxiosError) {
            errorMessage = error.response?.data?.message || errorMessage;
        } else if (error instanceof Error) {
            errorMessage = error.message || errorMessage;
        }
        toast.error(errorMessage);
        throw new Error(errorMessage);
    }
};
